import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '@/contexts/auth';

/**
 * Landing page for emailed sign-in links: /auth/magic?token=...
 * Verifies the single-use token, establishes the session, and forwards the
 * dreamer into the app.
 */
export function MagicLinkPage() {
  const { loginWithMagicToken } = useAuth();
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/auth/magic' }) as { token?: string };
  const [error, setError] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return; // tokens are single-use; never verify twice
    attempted.current = true;

    if (!token) {
      setError('This link is missing its sign-in token.');
      return;
    }

    loginWithMagicToken(token)
      .then(({ hasPassword }) => {
        navigate({ to: hasPassword ? '/dreams' : '/profile', search: hasPassword ? undefined : { welcome: 1 } });
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'This link is invalid or has expired');
      });
  }, [token, loginWithMagicToken, navigate]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="card">
          {error ? (
            <>
              <h1 className="text-2xl font-display font-bold text-white mb-3">Link expired</h1>
              <p className="text-dream-300 mb-4">{error}</p>
              <Link to="/login" className="btn-primary inline-block px-6 py-2">
                Request a new link
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-display font-bold text-white mb-3">Signing you in…</h1>
              <p className="text-dream-300">One moment while we verify your link.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
